import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { downloadExcel } from '@/lib/downloadExcel';
import { ArrowLeftRight, BarChart3, Boxes, Download, Layers, MapPin, Package, PackageCheck, Truck, type LucideIcon } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { SearchBox } from '@/components/form/SearchBox';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/form/Select';
import {
  listWarehouseSerials,
  listLocationOccupancy,
  warehouseSerialsExportPath,
  type WarehouseSerial,
  type LocationOccupancyRow,
} from '@/api/warehouseStock';
import { listLocations, type LocationLevel } from '@/api/locations';
import { formatDate } from '@/lib/format';

const LEVEL_TONE: Record<LocationLevel, string> = {
  Zone:  'bg-blue-500/15 text-blue-400 border-blue-500/30',
  Aisle: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  Rack:  'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Bin:   'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

type Tab = 'serials' | 'locations';

export function WarehouseSerialPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') === 'locations' ? 'locations' : 'serials') as Tab;
  const [tab, setTab] = useState<Tab>(initialTab);

  useEffect(() => {
    const cur = searchParams.get('tab');
    const desired = tab === 'locations' ? 'locations' : null;
    if (cur !== desired) {
      const next = new URLSearchParams(searchParams);
      if (desired) next.set('tab', desired);
      else next.delete('tab');
      setSearchParams(next, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  return (
    <div>
      <SectionHeader
        title="Item Serial Number Tracker"      />

      {/* Tab bar */}
      <div className="mb-2 inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1 sm:mb-2">
        <TabButton active={tab === 'serials'} onClick={() => setTab('serials')} label="Item Tracker" />
        <TabButton active={tab === 'locations'} onClick={() => setTab('locations')} label="Location Tracker" />
      </div>

      {tab === 'serials' ? <SerialsView /> : <LocationsView />}
    </div>
  );
}

function TabButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={
        'rounded-md px-3 py-1.5 text-[11px] font-bold transition sm:text-xs ' +
        (active
          ? 'bg-accent text-white shadow-sm'
          : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
      }
    >
      {label}
    </button>
  );
}

// ── SERIALS VIEW ─────────────────────────────────────────────────────────────
function SerialsView() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | 'InWarehouse' | 'Packed' | 'AtDepartment' | 'Dispatched'>('');
  const [locationId, setLocationId] = useState<'' | number>('');

  const query = useQuery({
    queryKey: ['wh-serials', search, status, locationId],
    placeholderData: (prev) => prev, // keep the current results on screen while the next search loads
    queryFn: () =>
      listWarehouseSerials({
        search: search.trim() || undefined,
        status: status || undefined,
        locationId: typeof locationId === 'number' ? locationId : undefined,
      }),
  });

  const locationsQuery = useQuery({
    queryKey: ['locations-flat'],
    queryFn: () => listLocations({}),
    staleTime: 60_000,
  });
  const locations = locationsQuery.data ?? [];

  const data = query.data?.rows ?? [];
  const byStatus = query.data?.byStatus;
  const total = query.data?.total ?? 0;
  const inWarehouseCount = byStatus?.InWarehouse ?? 0;
  const packedCount      = byStatus?.Packed ?? 0;
  const dispatchedCount  = byStatus?.Dispatched ?? 0;
  const atDeptCount      = byStatus?.AtDepartment ?? 0;
  // Total for the current view: grand total, or the selected status's true count.
  const shownTotal = status ? (byStatus?.[status] ?? data.length) : total;
  // "Total" KPI = units still on hand (In Warehouse + Packed). Dispatched serials
  // have physically left, so they're excluded — the records stay in the DB (for
  // sales returns/traceability) but don't inflate the on-hand count.
  const onHandTotal = inWarehouseCount + packedCount;

  // Export every serial matching the current filters (the list on screen stops at 2,000).
  const toast = useToast();
  const [exporting, setExporting] = useState(false);
  async function exportExcel() {
    setExporting(true);
    try {
      await downloadExcel(
        warehouseSerialsExportPath({
          search: search.trim() || undefined,
          status: status || undefined,
          locationId: typeof locationId === 'number' ? locationId : undefined,
        }),
        'GRHAMOY_SerialTracker.xlsx',
        180_000,
      );
    } catch (e) {
      toast.push(apiError(e, 'Export failed'), 'error');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      {/* KPI cards */}
      <div className="mb-2 grid grid-cols-2 gap-1.5 sm:mb-2 sm:gap-3 md:grid-cols-5">
        <KpiCard label="Total"        value={onHandTotal.toLocaleString()}     tone="info"    icon={Package} />
        <KpiCard label="In Warehouse" value={inWarehouseCount.toLocaleString()} tone="success" icon={PackageCheck} />
        <KpiCard label="Packed"       value={packedCount.toLocaleString()}    tone="primary" icon={Boxes} />
        <KpiCard label="At Department" value={atDeptCount.toLocaleString()}   tone="accent"  icon={ArrowLeftRight} />
        <KpiCard label="Dispatched"   value={dispatchedCount.toLocaleString()} tone="warning" icon={Truck} />
      </div>

      <FilterBar>
        <FilterField label="Search" className="col-span-3 sm:col-auto">
          <SearchBox onSearch={setSearch} placeholder="Serial no., SKU or item name" label="Search serials or items" busy={query.isFetching} inputClassName="sm:min-w-[260px]" />
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as '' | 'InWarehouse' | 'Packed' | 'AtDepartment' | 'Dispatched')}>
            <option value="">All</option>
            <option value="InWarehouse">In Warehouse</option>
            <option value="Packed">Packed</option>
            <option value="AtDepartment">At Department</option>
            <option value="Dispatched">Dispatched</option>
          </Select>
        </FilterField>
        <FilterField label="Location">
          <Select
            value={locationId === '' ? '' : String(locationId)}
            onChange={(e) => setLocationId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">All locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.level} — {loc.code} · {loc.name}
              </option>
            ))}
          </Select>
        </FilterField>
        <button
          type="button"
          onClick={exportExcel}
          disabled={exporting || shownTotal === 0}
          title={`Export all ${shownTotal.toLocaleString()} serial(s) matching these filters to Excel`}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent disabled:opacity-60 sm:col-span-1 sm:ml-auto sm:h-8 sm:gap-1.5 sm:self-end sm:px-3 sm:text-xs"
        >
          <Download size={12} /> {exporting ? 'Exporting…' : 'Export'}
        </button>
      </FilterBar>

      <Table<WarehouseSerial>
        paginated
        title="Serialized Units"
        count={`Showing ${data.length.toLocaleString()}${shownTotal > data.length ? ` of ${shownTotal.toLocaleString()}` : ''}`}
        rows={data}
        rowKey={(r) => r.id}
        storageKey="serial-tracker-units"
        loading={query.isFetching && data.length === 0}
        columns={[
          {
            key: 'img',
            header: '',
            width: '44px',
            align: 'center',
            alwaysVisible: true,
            cell: (r) => <ItemThumb src={r.item.imageUrl} size={28} />,
          },
          {
            key: 'serial',
            header: 'Serial No',
            cell: (r) => <span className="text-xs font-extrabold text-accent">{r.serialNo}</span>,
          },
          {
            key: 'item',
            header: 'Item',
            // Item code only; the name is in the tooltip (it mostly repeated the code).
            cell: (r) => <span className="font-bold text-xs" title={r.item.name}>{r.item.code}</span>,
          },
          {
            key: 'barcode',
            header: 'SKU Barcode',
            cell: (r) => r.item.barcode ?? <span style={{ color: 'var(--text-muted)' }}>—</span>,
          },
          { key: 'category', header: 'Category', cell: (r) => r.item.category ?? '—' },
          {
            key: 'location',
            header: 'Location',
            cell: (r) =>
              r.location ? (
                <div className="flex items-center justify-center gap-1.5">
                  <span
                    className={
                      'inline-flex flex-shrink-0 items-center rounded border px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider ' +
                      LEVEL_TONE[r.location.level as LocationLevel]
                    }
                  >
                    {r.location.level}
                  </span>
                  <div className="text-center">
                    <div className="text-xs font-bold">{r.location.code}</div>
                    <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{r.location.name}</div>
                  </div>
                </div>
              ) : r.department ? (
                <span className="text-xs font-bold" title="Out at this department (Dept Transfer)">Dept: {r.department}</span>
              ) : (
                <span style={{ color: 'var(--text-muted)' }}>—</span>
              ),
          },
          {
            key: 'grn',
            header: 'GRN / Source',
            cell: (r) =>
              r.grn ? (
                <div>
                  <div className="text-xs text-accent">{r.grn.grnNo}</div>
                  <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{formatDate(r.grn.grnDate)}</div>
                </div>
              ) : r.source === 'Adjustment' ? (
                <span className="rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-bold text-sky-600">Stock Adjustment</span>
              ) : (
                <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-500">Opening Stock</span>
              ),
          },
          {
            key: 'receivedAt',
            header: 'Received',
            cell: (r) => formatDate(r.receivedAt),
          },
          {
            key: 'status',
            header: 'Status',
            cell: (r) => (
              <Badge tone={r.status === 'InWarehouse' ? 'success' : r.status === 'Packed' ? 'info' : r.status === 'AtDepartment' ? 'accent' : 'warning'}>
                {r.status === 'InWarehouse' ? 'In Warehouse' : r.status === 'Packed' ? 'Packed' : r.status === 'AtDepartment' ? 'At Department' : 'Dispatched'}
              </Badge>
            ),
          },
          {
            key: 'dispatchedAt',
            header: 'Dispatched On',
            cell: (r) => r.dispatchedAt ? formatDate(r.dispatchedAt) : '—',
          },
        ]}
      />
    </>
  );
}

// ── LOCATIONS VIEW ───────────────────────────────────────────────────────────
function LocationsView() {
  const [search, setSearch] = useState('');
  const [nonEmptyOnly, setNonEmptyOnly] = useState(true);

  const query = useQuery({
    queryKey: ['location-occupancy', search, nonEmptyOnly],
    placeholderData: (prev) => prev,
    queryFn: () => listLocationOccupancy({ search: search || undefined, nonEmptyOnly }),
    refetchInterval: 30_000,
  });
  const rows = query.data ?? [];

  const kpis = useMemo(() => {
    const totalBins = rows.length;
    const totalCapacity = rows.reduce((s, r) => s + r.capacity, 0);
    const totalOccupied = rows.reduce((s, r) => s + r.occupied, 0);
    const totalFree     = rows.reduce((s, r) => s + r.free, 0);
    const avgUtil = totalCapacity > 0 ? Math.round((totalOccupied / totalCapacity) * 1000) / 10 : 0;
    return { totalBins, totalCapacity, totalOccupied, totalFree, avgUtil };
  }, [rows]);

  return (
    <>
      {/* KPI cards */}
      <div className="mb-2 grid grid-cols-2 gap-1.5 sm:mb-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-5">
        <KpiCard label="Locations Shown" value={kpis.totalBins.toLocaleString()}     tone="info"    icon={MapPin} />
        <KpiCard label="Total Peak"      value={kpis.totalCapacity.toLocaleString()} tone="primary" icon={Layers} />
        <KpiCard label="Total Occupied"  value={kpis.totalOccupied.toLocaleString()} tone="warning" icon={Boxes} />
        <KpiCard label="Total Free"      value={kpis.totalFree.toLocaleString()}     tone="success" icon={PackageCheck} />
        <KpiCard label="Avg Utilization" value={kpis.avgUtil > 0 ? `${kpis.avgUtil}%` : '—'} tone="accent" icon={BarChart3} />
      </div>

      <FilterBar>
        <FilterField label="Search">
          <SearchBox onSearch={setSearch} placeholder="Location code or name" label="Search locations" busy={query.isFetching} inputClassName="sm:min-w-[260px]" />
        </FilterField>
        <FilterField label="Show" className="col-span-2 sm:col-auto">
          <Select value={nonEmptyOnly ? 'active' : 'all'} onChange={(e) => setNonEmptyOnly(e.target.value === 'active')}>
            <option value="active">Locations that ever held stock</option>
            <option value="all">All locations</option>
          </Select>
        </FilterField>
      </FilterBar>

      <Table<LocationOccupancyRow>
        paginated
        title="Location Occupancy"
        count={`Showing ${rows.length}`}
        rows={rows}
        rowKey={(r) => r.locationId}
        storageKey="serial-tracker-bins"
        loading={query.isFetching && rows.length === 0}
        columns={[
          {
            key: 'location',
            header: 'Location',
            cell: (r) => (
              <div className="flex items-center gap-1.5">
                <span
                  className={
                    'inline-flex flex-shrink-0 items-center rounded border px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider ' +
                    LEVEL_TONE.Bin
                  }
                >
                  {r.level}
                </span>
                <span className="text-xs font-extrabold text-accent">{r.code}</span>
                {!r.active && <span className="text-[10px] font-bold uppercase text-[var(--text-muted)]">inactive</span>}
                {r.parentPath && (
                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    {r.parentPath}
                  </span>
                )}
              </div>
            ),
          },
          { key: 'name', header: 'Name', cell: (r) => <span className="text-xs">{r.name}</span> },
          {
            key: 'capacity',
            header: 'Peak',
            align: 'right',
            cell: (r) => <span className="font-bold text-sm">{r.capacity}</span>,
          },
          {
            key: 'occupied',
            header: 'Occupied',
            align: 'right',
            cell: (r) => (
              <span className={'font-bold text-sm ' + (r.occupied === 0 ? 'text-[var(--text-muted)]' : 'text-accent')}>
                {r.occupied}
              </span>
            ),
          },
          {
            key: 'free',
            header: 'Free',
            align: 'right',
            cell: (r) => (
              <span className={'font-bold text-sm ' + (r.free > 0 ? 'text-emerald-500' : 'text-[var(--text-muted)]')}>
                {r.free}
              </span>
            ),
          },
          {
            key: 'util',
            header: 'Utilization',
            cell: (r) => <UtilBar util={r.utilizationPct} />,
          },
        ]}
      />
    </>
  );
}

const KPI_ACCENT: Record<'info' | 'success' | 'warning' | 'primary' | 'accent', { color: string; bg: string }> = {
  info:    { color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' },
  success: { color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  primary: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)' },
  accent:  { color: '#FF8C00', bg: 'rgba(255,140,0,0.10)' },
};

function KpiCard({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  tone: 'info' | 'success' | 'warning' | 'primary' | 'accent';
  icon: LucideIcon;
}) {
  const { color } = KPI_ACCENT[tone];
  return (
    <div
      className="relative overflow-hidden rounded-lg p-2 text-white shadow-sm transition-transform hover:-translate-y-0.5 sm:rounded-xl sm:p-4"
      style={{ background: color, textShadow: KPI_TEXT_SHADOW }}
    >
      <Icon className="absolute -bottom-3 -right-3 h-16 w-16 sm:h-20 sm:w-20" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
      <div className="relative">
        <div className="text-[9px] font-extrabold uppercase tracking-widest text-white/90 sm:text-[10px]">{label}</div>
        <div className="mt-0.5 text-lg font-extrabold sm:mt-2 sm:text-2xl">{value}</div>
      </div>
    </div>
  );
}

function UtilBar({ util }: { util: number | null }) {
  if (util === null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const pct = Math.min(100, Math.max(0, util));
  const color =
    pct >= 90 ? '#ef4444' :
    pct >= 70 ? '#f59e0b' :
    pct >= 30 ? '#10b981' :
    '#94a3b8';
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="relative flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg-soft)' }}>
        <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-bold w-12 text-right" style={{ color }}>{util}%</span>
    </div>
  );
}
